/**
 * Open an existing Outlook message in a new inspector. Office.js cannot select
 * a row in the folder list; `displayMessageForm` is the supported hop.
 *
 * WHAT THIS FIXES: THE SILENCE
 * ----------------------------
 * The whole body used to be wrapped in a `catch` that only `console.warn`ed,
 * and `displayMessageForm` reports nothing at all when its id resolves to no
 * message. So a click on "Open Email" could produce no window, no error, and no
 * way to tell a broken feature from a bad link. This returns a result the caller
 * renders instead of deciding on its own that saying nothing is acceptable.
 *
 * WHAT IT DOES NOT DO: JUDGE THE ID
 * ---------------------------------
 * An earlier revision of this file classified ids by the leading byte of their
 * base64 decoding ("AAMk…" = 0x00 vs "AQMk…" = 0x01) and REFUSED the 0x01 form
 * as unopenable. That was wrong, and it is worth recording why so nobody
 * rebuilds it:
 *
 *   - The brief that could not be opened held `REST-FWD` — an eight-character
 *     INTEGRATION-TEST FIXTURE id that had leaked into the shared dev database.
 *     It was never a real Graph id and could never open.
 *   - The 0x01 ids blamed for it are real and current. `GET /users/{mb}/
 *     messages/{id}` resolves them, and Graph's own `translateExchangeIds`
 *     answers `restId -> ewsId` with the SAME id, so 0x01 is not some
 *     unconvertible second format.
 *   - The prefix difference is just two different messages. It is not a
 *     REST-vs-EWS distinction and carries no signal about openability.
 *
 * The refusal therefore blocked every working link in the queue. A test-data
 * artifact was diagnosed as a format bug, and the "fix" was worse than what it
 * replaced. If a brief will not open, look at whether its id is REAL before
 * theorising about its shape.
 *
 * `convertToEwsId` is still attempted because a REST-formatted id genuinely
 * needs it, and it is harmless otherwise: on an id it does not recognise it
 * returns the input UNCHANGED rather than throwing (verified against all three
 * RestVersion values). That silent pass-through is a second reason this function
 * used to fail invisibly, and is why the result below is explicit.
 */

/** Why an open did not happen. */
export type OpenMessageFailure = "no_id" | "host_cannot_open" | "open_failed";

export type OpenMessageResult =
  | { ok: true }
  | { ok: false; reason: OpenMessageFailure; message: string };

/** Copy for each failure. Written for an AM, not for whoever wrote the id. */
const FAILURE_COPY: Record<OpenMessageFailure, string> = {
  no_id: "This brief has no link back to the email.",
  host_cannot_open: "This version of Outlook cannot open another message from here.",
  open_failed: "Outlook could not open this email.",
};

export function openMessageInOutlook(graphMessageId: string): OpenMessageResult {
  const itemId = (graphMessageId || "").trim();
  if (!itemId) {
    return { ok: false, reason: "no_id", message: FAILURE_COPY.no_id };
  }

  // `Office?.…` is NOT enough. Optional chaining guards an identifier that is
  // DECLARED and undefined; in the stub/demo mode this pane also runs in,
  // `Office` is not declared at all, and a bare read of it throws
  // ReferenceError before the `?.` is ever consulted — turning the graceful
  // result this whole function exists to return back into the silence it
  // replaced. `typeof` is the only read that is safe on an undeclared name, and
  // it is the guard `main.tsx`, `auth.ts`, `oraWeb.ts` and `authClient.ts`
  // already use for exactly this case.
  const mailbox = (
    typeof Office !== "undefined" ? Office?.context?.mailbox : undefined
  ) as
    | {
        convertToEwsId?: (id: string, ver: Office.MailboxEnums.RestVersion) => string;
        displayMessageForm?: (itemId: string) => void;
      }
    | undefined;
  if (!mailbox?.displayMessageForm) {
    return {
      ok: false,
      reason: "host_cannot_open",
      message: FAILURE_COPY.host_cannot_open,
    };
  }

  // Same reason as above: read the enum only once `Office` is known to exist.
  const restVersion =
    typeof Office !== "undefined"
      ? Office?.MailboxEnums?.RestVersion?.v2_0
      : undefined;

  let resolved = itemId;
  try {
    if (mailbox.convertToEwsId && restVersion !== undefined) {
      resolved = mailbox.convertToEwsId(itemId, restVersion);
    }
  } catch {
    // An id the converter will not take is passed through as-is: it may already
    // be in a form displayMessageForm accepts.
    resolved = itemId;
  }

  try {
    mailbox.displayMessageForm(resolved);
  } catch (err) {
    console.warn("[ready-briefs] open in Outlook failed", err);
    return { ok: false, reason: "open_failed", message: FAILURE_COPY.open_failed };
  }
  // NOT a promise that a window appeared. `displayMessageForm` is fire-and-
  // forget and reports nothing when its id resolves to no message, so `ok`
  // means "the host accepted the call" and no more. An id that is real but
  // stale — the message hard-deleted since the brief was prepared — still lands
  // here and still opens nothing. That case is not detectable from this side.
  return { ok: true };
}
